import { useForm, Controller, type ControllerRenderProps } from 'react-hook-form'
import { useEffect, type FC } from 'react'
import cx from 'classnames'
import { twMerge } from 'tailwind-merge'
import { Input } from '../input/input'
import { RadioGroup } from '../radio-group/radio-group'
import { Button } from '../button/button'
import { Alert, AlertType } from '../alert/alert'
import type { EnrollmentFormProps, EnrollmentFormField, FieldValues } from './enrollment-form.types'

/**
 * EnrollmentForm - A reusable form component for enrollment flows
 *
 * Supports text inputs and radio button groups.
 *
 * @example
 * ```tsx
 * <EnrollmentForm
 *   fields={[
 *     [
 *       { name: 'firstName', label: 'First Name', type: 'text', required: true },
 *       { name: 'lastName', label: 'Last Name', type: 'text', required: true }
 *     ],
 *     [
 *       { name: 'email', label: 'Email', type: 'text', required: true }
 *     ],
 *     [
 *       {
 *         name: 'employmentStatus',
 *         label: 'Employment Status',
 *         type: 'radio',
 *         required: true,
 *         options: [
 *           { value: 'EMPLOYED', label: 'Employed' },
 *           { value: 'UNEMPLOYED', label: 'Unemployed' }
 *         ]
 *       }
 *       { name: 'email', label: 'Email', type: 'email', required: true }
 *     ]
 *   ]}
 *   onSubmit={handleSubmit}
 *   submitButtonText="Continue"
 *   showMobileFixedButton={true}
 * />
 * ```
 */
export const EnrollmentForm: FC<EnrollmentFormProps> = ({
  fields,
  onSubmit,
  defaultValues = {},
  submitButtonText = 'Submit',
  isSubmitting = false,
  submitError,
  showMobileFixedButton = false,
  mode = 'onChange',
  beforeSubmitButton,
  afterFields,
  onValidationChange,
  onValuesChange,
  className,
  id,
  testId,
  ariaLabel,
}) => {
  // Initialize form with react-hook-form
  const {
    control,
    handleSubmit: rhfHandleSubmit,
    reset,
    watch,
    formState: { errors: formErrors, isValid, touchedFields },
  } = useForm<FieldValues>({
    defaultValues,
    mode,
  })

  // Update form values when defaultValues change
  useEffect(() => {
    if (Object.keys(defaultValues).length > 0) {
      reset(defaultValues)
    }
  }, [defaultValues, reset])

  // Notify parent of validation state changes
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid)
    }
  }, [isValid, onValidationChange])

  // Notify parent of form value changes
  useEffect(() => {
    if (onValuesChange) {
      const subscription = watch((values) => {
        onValuesChange(values as FieldValues)
      })
      return () => subscription.unsubscribe()
    }
  }, [watch, onValuesChange])

  // Form submission handler
  const handleFormSubmit = rhfHandleSubmit((data: FieldValues) => {
    if (isValid) {
      void Promise.resolve(onSubmit(data))
    }
  })

  /**
   * Renders a single form field with Controller
   */
  const renderField = (field: EnrollmentFormField): JSX.Element => {
    const {
      name,
      type,
      label,
      placeholder,
      required = false,
      disabled = false,
      validationRules = {},
      helperText,
      isPII = false,
      testId: fieldTestId,
      className: fieldClassName,
      autoComplete,
      onInput,
      formatValue,
      options,
    } = field

    // Combine required rule with custom validation rules
    const rules = {
      ...validationRules,
      ...(required && { required: validationRules.required || 'This field is required' }),
    }

    return (
      <Controller
        key={name}
        name={name}
        control={control}
        rules={rules}
        render={({ field: controllerField }: { field: ControllerRenderProps<FieldValues, string> }) => {
          // Render radio group for radio type
          if (type === 'radio') {
            return (
              <div className={twMerge('w-full', fieldClassName)}>
                {label ? <p className="typo-primary-base-semibold mb-sm text-text-header">{label}</p> : null}
                <RadioGroup
                  {...controllerField}
                  options={options ?? []}
                  name={name}
                  testId={fieldTestId}
                  className="!py-xs typo-primary-base"
                  disabled={disabled}
                />
                {helperText && !formErrors[name] ? (
                  <p className="typo-primary-sm-regular text-text-meta mt-2xs">{helperText}</p>
                ) : null}
                {touchedFields[name] && formErrors[name] ? (
                  <p className="typo-primary-sm-regular text-text-error mt-2xs">
                    {formErrors[name]?.message as string}
                  </p>
                ) : null}
              </div>
            )
          }

          // Render text input for text type
          const fieldValue = String(controllerField.value ?? '')
          const displayValue = formatValue ? formatValue(fieldValue) : fieldValue

          return (
            <div className={twMerge('w-full', fieldClassName)}>
              <Input
                {...controllerField}
                value={displayValue}
                label={label}
                placeholder={placeholder}
                required={required}
                error={touchedFields[name] ? (formErrors[name]?.message as string | undefined) : undefined}
                type={type}
                testId={fieldTestId}
                isPII={isPII}
                autoComplete={autoComplete}
                onInput={onInput}
              />
              {helperText && !formErrors[name] ? (
                <p className="typo-primary-sm-regular text-text-meta mt-2xs">{helperText}</p>
              ) : null}
            </div>
          )
        }}
      />
    )
  }

  /**
   * Renders a row of fields (side-by-side on desktop)
   */
  const renderFieldRow = (row: EnrollmentFormField[], rowIndex: number): JSX.Element => {
    const isSingleField = row.length === 1

    return (
      <div
        key={`row-${String(rowIndex)}`}
        className={cx('flex flex-col mb-sm', !isSingleField && 'md:flex-row md:gap-md')}
      >
        {row.map((field) => renderField(field))}
      </div>
    )
  }

  return (
    <>
      <form
        onSubmit={(e) => void handleFormSubmit(e)}
        className={twMerge('w-full flex-1 flex flex-col justify-start', className)}
        id={id}
        data-testid={testId}
        aria-label={ariaLabel}
      >
        {/* Render all field rows */}
        {fields.map((row, index) => renderFieldRow(row, index))}

        {/* Additional content after fields */}
        {afterFields}

        {/* Additional content before submit button */}
        {beforeSubmitButton}

        {/* Desktop submit button */}
        <div className="hidden md:block">
          <Button
            type="submit"
            className="w-full md:w-min mb-5xl"
            isDisabled={!isValid}
            isLoading={isSubmitting}
            testId="primaryAction_button"
          >
            {submitButtonText}
          </Button>
        </div>

        {/* Error message */}
        {submitError ? <Alert alertType={AlertType.Error} description={submitError} className="mt-sm" /> : null}
      </form>

      {/* Mobile fixed bottom button */}
      {showMobileFixedButton ? (
        <div className="fixed bottom-0 left-0 right-0 z-50 flex justify-center px-lg py-xl bg-white shadow-[0_-2px_4px_-1px_rgba(0,0,0,0.05)] md:hidden">
          <Button
            type="submit"
            className="w-full"
            isDisabled={!isValid}
            isLoading={isSubmitting}
            testId="primaryAction_button"
            onClick={(e) => {
              e.preventDefault()
              if (isValid) {
                void handleFormSubmit(e)
              }
            }}
          >
            {submitButtonText}
          </Button>
        </div>
      ) : (
        // Non-fixed mobile button (when showMobileFixedButton is false)
        <div className="block md:hidden">
          <Button
            type="submit"
            className="w-full mb-5xl"
            isDisabled={!isValid}
            isLoading={isSubmitting}
            testId="primaryAction_button"
          >
            {submitButtonText}
          </Button>
        </div>
      )}
    </>
  )
}

export type { EnrollmentFormProps, EnrollmentFormField, FieldValues, RadioOption } from './enrollment-form.types'
